import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Item, Segment } from 'semantic-ui-react';
import ComponentProvider from '../../cnr/contexts/ComponentContext';
import { DisplayContext } from '../../cnr/contexts/DisplayContext';
import { FilterContext } from '../../cnr/contexts/FilterContext';
import { FrameworkContext } from '../../cnr/contexts/FrameworkContent';
import { ParentContext } from '../../cnr/contexts/ParentContext';
import { UiItemContext } from '../../cnr/contexts/UiItemContext';
import { formatItem, formatTypes } from '../../common/dateFormatting';
import { filterHelpers } from '../../common/filtering';
import { uniqueKey } from '../../common/keys';
import { gEnums } from '../../enums/globalEnums';
import { _animateTypes } from '../../motions/AnimateComponent';
import MotionComponent from '../../motions/MotionComponent';
import MenuSidebar from '../../sidebars/MenuSidebar';
import NoData from '../alerts/NoData';
import ErrorBoundaryWithNavigate from '../errorHandling/ErrorBoundaryWithNavigate';
import TableView from '../tables/TableView';
import { SemCardsWithProvider } from './SemCards';
import SemSchedule from './SemSchedule';
import ItemMenuSquares from 'global/wrappers/ItemMenuSquares';

const CalendarSidebar = React.lazy(() => import('../../sidebars/CalendarSidebar'));
const SemanticItems = React.lazy(() => import('./SemItems'));
const SemElemsAlt = React.lazy(() => import('./SemElemsAlt'));
const SemList = React.lazy(() => import('./SemList'));
const SimpleCarosel = React.lazy(() => import('./SemCarosels'));

// PageAreaContent > PageContent > PageItem > UiItem > SemUi > SemElems > SemCardsWithProvider

/**
 * 
 * @returns Creates and returns semanticUI element for the ui, called mainly from SemUI.js
 */
const SemElems = () => {

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, fns } = parentContext ?? {}
  const { paps_state, preview_state, transition_state } = states
  const { appUser_fns, navigation_fns } = fns ?? {}
  const { previewStatus } = preview_state ?? {}
  const { pathViews } = paps_state ?? {}

  const isSuperAdminDeveloper = appUser_fns.validateAccess_developer()

  const { transitions } = transition_state ?? {}
  const transition_pageItem = transitions ? transitions[_animateTypes.pageItem] : null
  const { showTransition: showTransition_pageItem } = transition_pageItem ?? {}

  // frameworkContext
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state } = frameworkContext ?? {}
  const { desktopMode } = framework_state ?? {}

  // uiItemContext
  const uiItemContext = useContext(UiItemContext);
  const { item_state } = uiItemContext ?? {}
  const { _cnc: cnc, viewItem, vld: viewListData, _groupingOpts } = item_state ?? {}

  const { display, desktopDisplay, maps, dataSource } = viewItem ?? {}

  const { dataSourceType } = dataSource ?? {}
  const _display = desktopMode && desktopDisplay ? desktopDisplay : display
  const { groupActive, groupType } = _groupingOpts ?? {}

  let ignoreGroups;

  let { displayType, cardsPerRow, imageProp, caption } = _display ?? {}

  // filterContext
  const filterContext = useContext(FilterContext);
  const { uiFilter_state } = filterContext ?? {}
  const { groupDataProps } = uiFilter_state ?? {}

  // displayContext
  const displayContext = useContext(DisplayContext);
  const { display_state } = displayContext ?? {}
  const { displayProps } = display_state ?? {}
  const { groups, selectedGroupItem } = displayProps ?? {}

  const viewItemType = viewItem?.key
  const vit = viewItemType

  const [initData, setInitData] = useState({ vld: null, dataFetched: false });
  const { vld, dataFetched } = initData;

  const viewListCount = useMemo(() => (vld ? Object.keys(vld).length : 0), [vld, pathViews]);

  const vld_allowed = useMemo(() => {
    let _vld = Object.assign({}, viewListData);
    let _isGrouped = false;

    if (groupActive && groups && selectedGroupItem) {
      _isGrouped = true;
      const gd = filterHelpers.find(groups, 'key', selectedGroupItem);
      _vld = gd?.data ? Object.assign({}, gd.data) : Object.assign({}, viewListData);
    }

    return !isSuperAdminDeveloper ? _.filter(_vld, (i) => i.name !== 'Test') : _vld;
  }, [viewListData, groupActive, groups, selectedGroupItem, isSuperAdminDeveloper, pathViews]);

  useEffect(() => {
    setInitData({ vld: vld_allowed, dataFetched: true });
  }, [vld_allowed]);

  if (displayType === gEnums.displayTypes.item) { displayType = gEnums.displayTypes.card }

  const getItemsPerRow = () => {
    if (!desktopMode) return 1;
    return cardsPerRow || Math.min(viewListCount, 3);
  };

  const renderContentByDisplayType = (contentData) => {

    if (viewItem?.semWrapper && displayType === gEnums.displayTypes.card) {
      return <SimpleCarosel />;
    }

    switch (displayType) {

      case gEnums.displayTypes.usaMap:
        return <div>USA MAP</div>

      case gEnums.displayTypes.list:
        return <SemList viewListData={contentData} />;

      case gEnums.displayTypes.squares:
        const hc = (item) => {
          navigation_fns.handleNav_item({ key: viewItem?.key }, item.key)
          // framework_handlers.handleClick_contentItem({ clickedItem: viewItem, itemKey: item.key })
        }
        return <ItemMenuSquares
          caption={_.startCase(viewItem?.key)}
          icon={'list'}
          menuItems={contentData}
          isFull={true}
          onClick={hc}
        ></ItemMenuSquares>

      case gEnums.displayTypes.card:
        return (
          <SemCardsWithProvider
            cardProps={{ vit, cnc, itemsPerRow: getItemsPerRow() }}
            cardListData={contentData}
          />
        );

      case gEnums.displayTypes.item:
        const cn = viewListCount === 1 ? 'seg-list' : 'seg-item-group';
        return (
          <Segment basic style={{ margin: 0, paddingTop: 0, paddingBottom: 0 }}>
            <Item.Group vit={vit} unstackable className={cn}>
              <SemanticItems viewListData={contentData} />
            </Item.Group>
          </Segment>
        );

      case gEnums.displayTypes.table:
      case gEnums.displayTypes.taggedTable:
        return (
          <TableView
            viewItem={viewItem}
            viewListData={contentData}
            allowClickTo={true}
          />
        );

      case gEnums.displayTypes.questionsAndAnswers:
        return null;

      default:
        return (
          <SemElemsAlt
            _display={_display}
            cnc={cnc}
            contentData={contentData}
            displayType={displayType}
            imageProp={imageProp}
            item_state={item_state}
            maps={maps}
            viewItem={viewItem}
            vit={vit}
          />
        );
    }
  };

  const content = (contentData) => {

    const isComponentOrPageData = dataSourceType === gEnums.dataSourceTypes.component ? true : false
    // ||
    // dataSourceType === gEnums.dataSourceTypes.pageDataCollection;

    if (displayType !== gEnums.displayTypes.questionsAndAnswers && !isComponentOrPageData && viewListCount === 0) {
      return <NoData viewItem={viewItem} noCaption />;
    }

    if (isComponentOrPageData) ignoreGroups = true;

    return renderContentByDisplayType(contentData);
  };

  const contentWrapper = (vld) => (
    <ComponentProvider>{content(vld)}</ComponentProvider>
  );

  const _contentWrapper = useMemo(() => {
    if (!dataFetched) return null;
    return contentWrapper(vld);
  }, [dataFetched, vld]);


  // ---------- Group Rendering Logic ----------

  if (groupActive && groupDataProps && groupType && !ignoreGroups) {
    const { groups, groupByProp } = groupDataProps ?? {};

    const renderGroupContents = () => {

      const contents = [];
      const menuKeys = [];

      groups?.forEach(({ key: groupKey, data }) => {
        if (!groupKey || !data) return;
        const formattedKey = groupByProp?.toLowerCase().includes('date')
          ? formatItem(formatTypes.fullDate, groupKey)
          : groupKey;

        menuKeys.push(formattedKey);
        contents.push(contentWrapper(data));
      });

      return { contents, menuKeys };
    };

    switch (groupType) {
      case gEnums.groupTypes.segment:
        return <Segment>TEST</Segment>;

      case gEnums.groupTypes.calendarSidebar: {
        const { contents, menuKeys } = renderGroupContents();
        return <CalendarSidebar prefix={viewItemType} menuKeys={menuKeys} segs={contents} />;
      }

      case gEnums.groupTypes.menuSidebar: {
        const { contents, menuKeys } = renderGroupContents();
        return <MenuSidebar prefix={viewItemType} menuKeys={menuKeys} segs={contents} />;
      }

      case gEnums.groupTypes.schedule:
      case gEnums.groupTypes.scheduleTop:
        return (
          <SemSchedule
            groupType={groupType}
            groupDataProps={groupDataProps}
            card_props={{ vit }}
          />
        );

      case gEnums.groupTypes.swipeableTab:
        return contentWrapper(vld);

      case gEnums.groupTypes.swipeableGroup:
        return groups?.map(({ key, data }) => (
          <div key={uniqueKey('sw', 'si', key)} className="swipeable-group-container">
            <div>{key}</div>
            {contentWrapper(data)}
          </div>
        )) ?? contentWrapper(vld);

      default:
        return <NoData altCaption="Group Type" />;
    }
  }

  // ---------- Final Return Logic ----------

  if (!dataFetched) return <div></div>;

  return showTransition_pageItem ? (
    <MotionComponent transition={transition_pageItem}>
      <ErrorBoundaryWithNavigate origin="SemElems">
        {dataFetched ? _contentWrapper : <div>---</div>}
      </ErrorBoundaryWithNavigate>
    </MotionComponent>
  ) : (
    _contentWrapper
  );
}

export default SemElems
